import type { ClientFile } from "@repo/shared/types";
import { type DragEvent, useEffect, useRef, useState } from "react";
import { ApiError, postFormData } from "@/lib/api/_client";

export const CLIENT_FILE_ACCEPT =
  "image/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv";

// Uploads files to a client one by one and reports per-file failures without
// aborting the batch. Also owns the drag-and-drop state for the drop target:
// spread `dragProps` onto the element that should accept dropped files.
export function useClientFileUpload({
  clientId,
  onUploaded,
  fallbackError,
}: {
  clientId: string;
  /** Called once when at least one file was stored. */
  onUploaded: () => void;
  /** Message used when the API error carries no message of its own. */
  fallbackError: string;
}) {
  const [isUploading, setIsUploading] = useState(false);
  // Mirror of isUploading for the drop handler, which may hold a stale closure.
  const uploading = useRef(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Depth counter: dragenter/dragleave fire on every descendant, so a plain
  // boolean would flicker while the pointer crosses child elements.
  const dragDepth = useRef(0);

  const uploadFiles = async (fileList: File[]) => {
    // One batch at a time: a second drop mid-upload would run concurrently and
    // interleave its errors with the first batch's.
    if (fileList.length === 0 || uploading.current) return;
    uploading.current = true;
    setIsUploading(true);
    setUploadError(null);
    const errors: string[] = [];
    let uploadedAny = false;
    for (const file of fileList) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await postFormData<ClientFile>(`/internal/client-files/${clientId}`, formData);
        uploadedAny = true;
      } catch (err) {
        const message = err instanceof ApiError ? err.message : fallbackError;
        errors.push(`${file.name}: ${message}`);
      }
    }
    if (uploadedAny) onUploaded();
    if (errors.length > 0) setUploadError(errors.join("\n"));
    uploading.current = false;
    setIsUploading(false);
  };

  // While the owner is mounted, a file dropped outside the drop target (e.g. on
  // an open editor) must not navigate the SPA to the file. The drop target's
  // own handlers run first and preventDefault, which is what the check reads.
  useEffect(() => {
    const swallow = (e: globalThis.DragEvent) => {
      if (e.defaultPrevented || !e.dataTransfer?.types.includes("Files")) return;
      e.preventDefault();
      if (e.type === "dragover") e.dataTransfer.dropEffect = "none";
    };
    document.addEventListener("dragover", swallow);
    document.addEventListener("drop", swallow);
    return () => {
      document.removeEventListener("dragover", swallow);
      document.removeEventListener("drop", swallow);
    };
  }, []);

  const hasFiles = (e: DragEvent) => e.dataTransfer.types.includes("Files");

  const dragProps = {
    onDragEnter: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setIsDragging(true);
    },
    onDragOver: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsDragging(false);
    },
    onDrop: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      void uploadFiles(Array.from(e.dataTransfer.files));
    },
  };

  return { uploadFiles, isUploading, uploadError, isDragging, dragProps };
}
